import React, { useEffect, useState } from "react";
import { ColorPicker } from "primereact/colorpicker";
import BoardData from "../boards/boardData";

import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Calendar } from "primereact/calendar";
import Cookies from "js-cookie";
import { CREATE_TASK_URL, DELETE_TASK_URL } from "../../global/API";
import Swal from "sweetalert2";
import { Dropdown } from "primereact/dropdown";

const userList = [
  { name: "Brahmaji", code: "BR" },
  { name: "Chandra", code: "CH" },
  { name: "Sunil", code: "SU" },
  { name: "Siva", code: "SI" },
  { name: "Mohan", code: "MO" },
];

const SortableColumn = ({ id, statusName, itemCount, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusHeaderMeta = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return {
          icon: "fa fa-check-circle",
          color: "green",
        };

      case "in progress":
        return {
          icon: "fa fa-adjust",
          color: "blue",
        };

      case "todo":
        return {
          icon: "ri-donut-chart-fill",
          color: "gray",
        };

      default:
        return {
          icon: "ri-donut-chart-fill",
          color: "gray",
        };
    }
  };
  const meta = getStatusHeaderMeta(statusName);

  return (
    <div className={`card-board ${meta.color}`} style={style}>
      <div className="card-board-header" ref={setNodeRef} {...attributes} {...listeners}>
        <h5 className={`header-title ${meta.color}`}>
          <i className={meta.icon}></i> &nbsp;
          {statusName}
        </h5>
        <span>{itemCount}</span>
        <button className="btn">
          <i className="ri-add-line"></i>
        </button>
      </div>
      {children}
    </div>
  );
};

function TaskBoards(props) {
  const { taskList, onClickTaskDetail, modifiedTaskDetails, setModifiedTaskDetails } = props;
  const [color, setColor] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [showSubtaskForm, setShowSubtaskForm] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Build columns state from taskList once
  const [columns, setColumns] = useState(() => {
    const cols = {};

    if (taskList?.statusList) {
      taskList.statusList.forEach((status) => {
        const columnId = status.task_status.replace(/\s+/g, "").toLowerCase();

        cols[columnId] = {
          name: status.task_status,
          items: taskList[columnId]?.data || [],
        };
      });
    }

    return cols;
  });

  // Maintain visual order of the columns for dnd-kit
  const [columnOrder, setColumnOrder] = useState(() => {
    if (!taskList?.statusList) return [];
    return taskList.statusList.map((status) => status.task_status.replace(/\s+/g, "").toLowerCase());
  });

  // Called from BoardData when dragging a task starts
  const handleDragStart = (columnId, item) => {
    setDraggedItem({ columnId, item });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedItem) return;

    const { columnId: sourceColumnId, item } = draggedItem;

    // safety
    if (!columns[sourceColumnId] || !columns[targetColumnId]) return;
    if (sourceColumnId === targetColumnId) return;

    setColumns((prev) => {
      const next = { ...prev };

      // remove from source column
      next[sourceColumnId] = {
        ...next[sourceColumnId],
        items: next[sourceColumnId].items.filter((i) => i.id !== item.id),
      };

      // add to target column
      next[targetColumnId] = {
        ...next[targetColumnId],
        items: [...next[targetColumnId].items, item],
      };

      return next;
    });

    setDraggedItem(null);
  };

  // Handle column reordering using dnd-kit
  const handleColumnDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setColumnOrder((prevOrder) => {
      const oldIndex = prevOrder.indexOf(active.id);
      const newIndex = prevOrder.indexOf(over.id);

      if (oldIndex === -1 || newIndex === -1) return prevOrder;

      return arrayMove(prevOrder, oldIndex, newIndex);
    });
  };

  const onHandleEvent = (event, setFunction) => {
    const { name, value } = event.target;
    setFunction((previousState) => ({ ...previousState, [name]: value }));
  };

  const handleTaskSubmit = async (e, status) => {
    e.preventDefault();

    const formData = new FormData();

    for (const key in modifiedTaskDetails) {
      formData.append(key, modifiedTaskDetails[key]);
    }
    formData.append("parent", 0);
    formData.append("assignee", 0);
    formData.append("task_status", status);

    try {
      const jwtToken = Cookies.get("jwtToken");
      const options = {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Accept: "application/json",
        },
      };

      const response = await fetch(CREATE_TASK_URL + "0", options);
      const data = await response.json();

      if (data.status === "Success") {
        Swal.fire({ title: "Success", text: "Task saved successfully.", icon: "success" });
        setShowTaskForm(null);
        setModifiedTaskDetails({});
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSubtaskSubmit = async (e, parentTask) => {
    e.preventDefault();

    const formData = new FormData();
    Object.keys(modifiedTaskDetails).forEach((key) => formData.append(key, modifiedTaskDetails[key]));

    formData.append("parent", parentTask.id);
    formData.append("assignee", 0);
    formData.append("task_status", parentTask.task_status);

    try {
      const jwtToken = Cookies.get("jwtToken");

      const response = await fetch(CREATE_TASK_URL + 0, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      const data = await response.json();

      if (data.status === "Success") {
        setShowSubtaskForm(null);
        setModifiedTaskDetails({});
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const jwtToken = Cookies.get("jwtToken");
        const options = {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${jwtToken}`,
            Accept: "application/json",
          },
        };

        try {
          const response = await fetch(DELETE_TASK_URL + id, options);
          if (response.ok) {
          } else {
            console.error("Failed to delete task");
            Swal.fire({
              title: "Error",
              text: "Failed to delete task.",
              icon: "error",
            });
          }
        } catch (error) {
          console.error("Error deleting task:", error);
          Swal.fire({
            title: "Error",
            text: "Error deleting task.",
            icon: "error",
          });
        }
      }
    });
  };

  useEffect(() => {
    if (!taskList?.statusList) return;

    const cols = {};

    taskList.statusList.forEach((status) => {
      const columnId = status.task_status.replace(/\s+/g, "").toLowerCase();

      cols[columnId] = {
        name: status.task_status,
        items: taskList[columnId]?.data || [],
      };
    });

    setColumns(cols);
  }, [taskList]);

  return (
    <div className="board-grid">
      <DndContext collisionDetection={closestCenter} onDragEnd={handleColumnDragEnd}>
        {columnOrder.length > 0 && (
          <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
            {columnOrder.map((columnId) => {
              const column = columns[columnId] || { items: [] };

              // Find the status object for label (fallback to columnId if not found)
              const status = taskList.statusList?.find((s) => s.task_status.replace(/\s+/g, "").toLowerCase() === columnId);

              const statusName = status?.task_status || columnId;

              return (
                <SortableColumn key={columnId} id={columnId} statusName={statusName} itemCount={column.items.length}>
                  <div className="card-board-body" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, columnId)}>
                    {column.items.map((taskItem) => (
                      <React.Fragment key={taskItem.id}>
                        <BoardData
                          item={taskItem}
                          columnId={columnId}
                          handleDragStart={handleDragStart}
                          onClickTaskDetail={onClickTaskDetail}
                          onAddSubtask={(taskId) => {
                            setShowSubtaskForm(taskId);
                            setModifiedTaskDetails({});
                          }}
                        />

                        {showSubtaskForm === taskItem.id && (
                          <div className="board-sub">
                            <div className="board board-highlighted">
                              <form className="board-form" onSubmit={(e) => handleSubtaskSubmit(e, taskItem)}>
                                <div className="form-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Subtask Name..."
                                    name="title"
                                    autoFocus
                                    value={modifiedTaskDetails.title || ""}
                                    onChange={(e) => onHandleEvent(e, setModifiedTaskDetails)}
                                  />
                                </div>
                                <div className="form-group form-group-icon">
                                  <i className="ri-user-add-line"></i>
                                  <Dropdown
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.value)}
                                    options={userList}
                                    optionLabel="name"
                                    optionValue="code"
                                    placeholder="Add Assignee"
                                    filter
                                    collapseIcon="null"
                                    dropdownIcon="null"
                                    appendTo="self"
                                    className="board-customised-dropdown"
                                  />
                                </div>
                                <div className="form-group form-group-icon">
                                  <Calendar
                                    value={modifiedTaskDetails.dueDate || ""}
                                    onChange={(e) => {
                                      setModifiedTaskDetails((prev) => ({
                                        ...prev,
                                        dueDate: e.value,
                                      }));
                                    }}
                                    name="dueDate"
                                    className="board-form-calendar"
                                    readOnlyInput
                                    dateFormat="M dd yy"
                                    showButtonBar
                                    showIcon
                                    placeholder="Add Due date"
                                  />
                                </div>

                                <div className="btn-stack">
                                  <button type="submit" className="btn btn-save" disabled={!modifiedTaskDetails.title?.trim()}>
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-exit"
                                    onClick={() => {
                                      setShowSubtaskForm(null);
                                      setModifiedTaskDetails({});
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}

                    {showTaskForm === statusName && (
                      <div className="board board-highlighted">
                        <form className="board-form" onSubmit={(e) => handleTaskSubmit(e, statusName)}>
                          <div className="form-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Task Name..."
                              name="title"
                              autoFocus
                              value={modifiedTaskDetails.title || ""}
                              onChange={(e) => {
                                onHandleEvent(e, setModifiedTaskDetails);
                              }}
                            />
                          </div>
                          <div className="form-group form-group-icon">
                            <i className="ri-user-add-line"></i>
                            <Dropdown
                              value={selectedUser}
                              onChange={(e) => setSelectedUser(e.value)}
                              options={userList}
                              optionLabel="name"
                              optionValue="code"
                              placeholder="Add Assignee"
                              filter
                              collapseIcon="null"
                              dropdownIcon="null"
                              appendTo="self"
                              className="board-customised-dropdown"
                            />
                          </div>
                          <div className="form-group form-group-icon">
                            <Calendar
                              value={modifiedTaskDetails.dueDate || ""}
                              onChange={(e) => {
                                setModifiedTaskDetails((prev) => ({
                                  ...prev,
                                  dueDate: e.value,
                                }));
                              }}
                              name="dueDate"
                              className="board-form-calendar"
                              readOnlyInput
                              dateFormat="M dd yy"
                              showButtonBar
                              showIcon
                              placeholder="Add Due date"
                            />
                          </div>
                          <div className="btn-stack">
                            <button type="submit" className="btn btn-save" disabled={!modifiedTaskDetails.title || modifiedTaskDetails.title.trim() === ""}>
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-exit"
                              onClick={() => {
                                setShowTaskForm(null);
                                setModifiedTaskDetails({});
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    <button
                      className="btn board-task-add-btn"
                      onClick={() => {
                        setShowTaskForm(statusName);
                      }}
                    >
                      <i className="ri-add-line"></i> Add Task
                    </button>
                  </div>
                </SortableColumn>
              );
            })}
          </SortableContext>
        )}
      </DndContext>

      {/* Add group dropdown */}
      <div className="add-board-group">
        <button className="btn add-group-btn" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
          <i className="ri-add-line"></i> Add group
        </button>
        <div className="dropdown task-dropdown">
          <div className="dropdown-menu">
            <form className="dropdown-item">
              <ColorPicker value={color} onChange={(e) => setColor(e.value)} />
              <input type="text" className="form-control" placeholder="STATUS NAME" />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskBoards;
